<template>
    <div class="stacked-line-chart-container">
        <div ref="chartContainer" class="chart-container"></div>
    </div>
</template>

<script>
import * as echarts from 'echarts';
import { debounce } from 'lodash';

export default {
    name: 'StackedLineChart',
    props: {
        data: {
            type: Array,
            required: true
        },
        settings: {
            type: Object,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        fields: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            chart: null,
            resizeObserver: null
        };
    },
    computed: {
        xAxisField() {
            return this.fields.find(f => f._id === this.settings.xAxis.field);
        },
        yAxisField() {
            if (this.settings.yAxis.calculationType === 'count') return null;
            return this.fields.find(f => f._id === this.settings.yAxis.field);
        },
        stackByField() {
            if (!this.settings.stackBy || !this.settings.stackBy.field) return null;
            return this.fields.find(f => f._id === this.settings.stackBy.field);
        },
        is100Percent() {
            return this.settings.chartType === 'stacked-line-100';
        }
    },
    watch: {
        data: {
            handler: 'updateChart',
            deep: true
        },
        settings: {
            handler: 'updateChart',
            deep: true
        }
    },
    methods: {
        initChart() {
            if (this.chart) {
                this.chart.dispose();
            }

            // Ensure container exists and has dimensions
            if (!this.$refs.chartContainer || 
                !this.$refs.chartContainer.offsetWidth || 
                !this.$refs.chartContainer.offsetHeight) {
                return;
            }

            this.chart = echarts.init(this.$refs.chartContainer);
            this.updateChart();
            this.initResizeObserver();
        },
        initResizeObserver() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }

            this.resizeObserver = new ResizeObserver(debounce(() => {
                if (this.chart && this.$refs.chartContainer) {
                    requestAnimationFrame(() => {
                        this.chart.resize();
                    });
                }
            }, 100));

            if (this.$refs.chartContainer) {
                this.resizeObserver.observe(this.$refs.chartContainer);
            }
        },
        calculatePercentages(data) {
            const totals = data[0].data.map((_, index) => {
                return data.reduce((sum, series) => sum + (series.data[index] || 0), 0);
            });

            return data.map(series => ({
                ...series,
                data: series.data.map((value, index) => {
                    const total = totals[index];
                    return total === 0 ? 0 : (value / total) * 100;
                })
            }));
        },
        updateChart() {
            if (!this.chart || !this.data || !this.data.length || !this.stackByField) return;

            const xAxisData = this.data.map(item => item[this.xAxisField.name]);
            
            // Get unique stack groups
            const stackGroups = new Set();
            this.data.forEach(item => {
                Object.keys(item).forEach(key => {
                    if (key !== this.xAxisField.name) {
                        stackGroups.add(key);
                    }
                });
            });

            // Create series data
            let series = Array.from(stackGroups).map(group => {
                const data = this.data.map(item => item[group] || 0);
                const customLabel = this.$_.get(this.settings, `customization.labelCustomization.${group}`);
                
                return {
                    name: customLabel?.label || group,
                    type: 'line',
                    stack: 'total',
                    areaStyle: {},
                    emphasis: {
                        focus: 'series'
                    },
                    data: data,
                    lineStyle: {
                        color: customLabel?.color
                    },
                    itemStyle: {
                        color: customLabel?.color
                    }
                };
            });

            // Convert to percentages if needed
            if (this.is100Percent) {
                series = this.calculatePercentages(series);
            }

            // Add benchmark lines if configured
            const markLines = [];
            const benchmarkLines = this.settings?.customization?.benchmarkLines || this.settings?.benchmarkLines || [];
            if (benchmarkLines.length) {
                benchmarkLines.forEach(line => {
                    if (line.axis === 'y') {
                        markLines.push({
                            name: line.label,
                            yAxis: line.value,
                            label: {
                                show: true,
                                formatter: line.text || line.label,
                                position: 'end'
                            },
                            data: {
                                name: line.label,
                                yAxis: line.value,
                                lineStyle: {
                                    color: line.color,
                                    type: 'dashed',
                                    width: 2
                                },
                                label: {
                                    formatter: `${line.label}${line.text ? ': ' + line.text : ''}`,
                                    position: 'middle',
                                    show: true,
                                    color: line.color,
                                    fontSize: 12,
                                    backgroundColor: '#fff',
                                    padding: [4, 8]
                                }
                            }
                        });
                    }
                    if (line.axis === 'x') {
                        markLines.push({
                            name: line.label,
                            xAxis: line.value,
                            data: {
                                name: line.label,
                                xAxis: line.value,
                                lineStyle: {
                                    color: line.color,
                                    type: 'dashed',
                                    width: 2
                                },
                                label: {
                                    formatter: `${line.label}${line.text ? ': ' + line.text : ''}`,
                                    position: 'middle',
                                    show: true,
                                    color: line.color,
                                    fontSize: 12,
                                    backgroundColor: '#fff',
                                    padding: [4, 8]
                                }
                            }
                        });
                    }
                });
            }

            const option = {
                title: {
                    text: this.title,
                    left: 'center'
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'cross',
                        label: {
                            backgroundColor: '#6a7985'
                        }
                    },
                    formatter: (params) => {
                        let result = params[0].name + '<br/>';
                        params.forEach(param => {
                            const value = this.is100Percent 
                                ? param.value.toFixed(2) + '%'
                                : param.value;
                            result += param.marker + param.seriesName + ': ' + value + '<br/>';
                        });
                        return result;
                    }
                },
                legend: {
                    data: series.map(s => s.name),
                    top: 30
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '3%',
                    containLabel: true
                },
                xAxis: {
                    type: 'category',
                    boundaryGap: false,
                    data: xAxisData
                },
                yAxis: {
                    type: 'value',
                    axisLabel: {
                        formatter: this.is100Percent ? '{value}%' : '{value}'
                    },
                    max: this.is100Percent ? 100 : undefined
                },
                series: series.map(s => ({
                    ...s,
                    markLine: markLines.length ? {
                        symbol: ['none', 'none'],
                        silent: true,
                        data: markLines.map(x => x.data)
                    } : undefined
                }))
            };

            this.chart.setOption(option);
        }
    },
    mounted() {
        // Wait for container to be rendered
        this.$nextTick(() => {
            requestAnimationFrame(() => {
                this.initChart();
            });
        });
    },
    beforeDestroy() {
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }
};
</script>

<style lang="scss" scoped>
.stacked-line-chart-container {
    width: 100%;
    height: 100%;
    min-height: 200px;
    position: relative;

    .chart-container {
        width: 100%;
        height: 100%;
        min-height: 200px;
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    }
}
</style> 